import { FC, HTMLAttributes } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { CommonBall, CommonBallContainerProps } from '../CommonBall';

export type BallColor = 'red' | 'yellow' | 'blue' | 'gray';

export type Ball30Letter = 'R' | 'Y' | 'B' | 'G';

export type Ball30ColorProps = {
    color: BallColor;
};

export type Ball30Props = HTMLAttributes<HTMLDivElement> & {
    number: number;
} & CommonBallContainerProps;

const OVERLAY_SIZE = {
    big: css`
        width: 8.938rem;
        height: 8.938rem;
    `,
    small: css`
        width: 6.188rem;
        height: 6.188rem;
    `,
    xsmall: css`
        width: 4.25rem;
        height: 4.25rem;
    `,
    xxsmall: css`
        width: 2.75rem;
        height: 2.75rem;
    `,
};

const INNER_CIRCLE_SIZE = {
    big: css`
        width: 4.875rem;
        height: 4.875rem;
    `,
    small: css`
        width: 3.375rem;
        height: 3.375rem;
    `,
    xsmall: css`
        width: 2.313rem;
        height: 2.313rem;
    `,
    xxsmall: css`
        width: 1.5rem;
        height: 1.5rem;
    `,
};

const NUMBER_SIZE = {
    big: css`
        font-size: 3.625rem;
    `,
    small: css`
        font-size: 2.5rem;
    `,
    xsmall: css`
        font-size: 1.625rem;
    `,
    xxsmall: css`
        font-size: 1rem;
    `,
};

export const Ball30Overlay = styled.img``;

export const Ball30InnerCircle = styled.div`
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    font-family: Signika-Bold;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    background-color: ${({ theme }) => theme.ball80.background};
`;

export const Ball30Number = styled.div`
    color: ${({ theme }) => theme.ball80.color};
`;

export const Ball30Container = styled(CommonBall)<Ball30ColorProps>`
    ${({ color, size, theme }) => `
        border-color: ${theme.ball80[color].border};
        background-image: radial-gradient(
            circle at 50% 0,
            ${theme.ball80[color].backgroundGradient.first} 1%,
            ${theme.ball80[color].backgroundGradient.second} 15%,
            ${theme.ball80[color].backgroundGradient.third} 50%,
            ${theme.ball80[color].backgroundGradient.fourth} 120%
        );

        ${Ball30Overlay} {
            ${OVERLAY_SIZE[size]}
        }

        ${Ball30InnerCircle} {
            ${INNER_CIRCLE_SIZE[size]}
        }

        ${Ball30Number} {
            ${NUMBER_SIZE[size]}
        }
    `};
`;

export const LETTER_COLORS = {
    R: 'red',
    Y: 'yellow',
    B: 'blue',
    G: 'gray',
};

export const Ball30: FC<Ball30Props> = ({ number, size, ...props }) => {
    /**
        Default letter is needed because BallCalls Component will pass a number more than 80.
        It will cause an undefined value for letter.
    */
    // const RYBG = 'RYBG';
    // const letter = (RYBG[Math.floor((number - 1) / 20)] as Ball30Letter) || 'R';
    let color = LETTER_COLORS['R'] as BallColor;

    const theme = useTheme();
    const { ball80Overlay } = theme.images;

    return (
        <Ball30Container {...props} color={color} size={size}>
            <Ball30Overlay src={ball80Overlay} />
            <Ball30InnerCircle>
                <Ball30Number>{number}</Ball30Number>
            </Ball30InnerCircle>
        </Ball30Container>
    );
};
